import { useState, useCallback } from 'react';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { subscribeIdentity, unsubscribeIdentity } from '../api/marketplaceApi';

export interface UseSubscriptionResult {
  toggleSubscription: (channelId: string, isCurrentlySubscribed: boolean) => Promise<boolean>;
  isToggling: (channelId: string) => boolean;
  error: string | null;
}

/**
 * The personal "You" channel subscription. The subscriber is the calling
 * IDENTITY (self-subscribe) — it grants only you `view` on the channel's
 * templates, with no fan-out to your org or any sphere. (Org- and sphere-scoped
 * subscriptions are separate, explicit actions.)
 */
export function useSubscription(
  onOptimisticUpdate?: (channelId: string, subscribed: boolean) => void
): UseSubscriptionResult {
  const apiClients = useApiClientsOrNull();

  const [togglingMap, setTogglingMap] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const toggleSubscription = useCallback(
    async (channelId: string, isCurrentlySubscribed: boolean): Promise<boolean> => {
      if (!apiClients) {
        return false;
      }

      setTogglingMap((prev) => ({ ...prev, [channelId]: true }));
      setError(null);

      // Optimistic update: immediately flip subscription state
      if (onOptimisticUpdate) {
        onOptimisticUpdate(channelId, !isCurrentlySubscribed);
      }

      try {
        if (isCurrentlySubscribed) {
          await unsubscribeIdentity(apiClients.schemaClient, channelId);
        } else {
          await subscribeIdentity(apiClients.schemaClient, channelId);
        }
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to toggle subscription';
        setError(message);
        // Revert optimistic update on error
        if (onOptimisticUpdate) {
          onOptimisticUpdate(channelId, isCurrentlySubscribed);
        }
        return false;
      } finally {
        setTogglingMap((prev) => {
          const next = { ...prev };
          delete next[channelId];
          return next;
        });
      }
    },
    [apiClients, onOptimisticUpdate]
  );

  const isToggling = useCallback(
    (channelId: string): boolean => {
      return !!togglingMap[channelId];
    },
    [togglingMap]
  );

  return { toggleSubscription, isToggling, error };
}
